import React, {useContext} from 'react';
import PropTypes from 'prop-types';
import { graphql } from "gatsby";
import {generateSlug} from '../../lib/functions/util-functions';
import ContentBlock from '../contentBlock/contentBlock';
import HubSpotContent from '../form/hubspotFormContent/hubspotFormContent';
import Image from '../image/image';
import Link from '../link/link';
import {ConditionalWrapper} from '../../lib/functions/util-functions';
import { LangContext } from '../../context/LocaleContext';

/**
 * CMS Component: componentModuleTile
 * Tile Styles: Product Tile, Fade Tile, Hard Frame Tile
 * Color Profiles: Default, Reverse
 */

const Tile = ({ className, data, ...other}) => {
  const { getTranslation } = useContext(LangContext);

    /**
     * Args : any tile data (can be DatoCmsPostTypeCaseStudy, DatoCmsPostTypeNews, or DatoCmsPostType, DatoCmsComponentTile)
     * Implements any overrides, default styles & default tag
     * 
     */
    const convertPostTypeData = (data) => {
      //Data from tile fallbacks
      const {cardStyle, colorProfile, content : tileContent, fadeTileColor, image, postToDisplay } = data || {};
      //Data from post
      const {categories, featuredImage, meta, seo, slug, title} = postToDisplay || data || {};
      const {content, heading, eyebrow, linkS, ...formContent} = (tileContent && tileContent[0]) || {};
      
      let featured = false;
      let isForm = formContent?.form;

      //featured flag
      if(categories){
        //check for featured category
        featured = categories?.find((cat)=>{return cat.slug === process.env.GATSBY_FEATURED_CAT});
        featured = featured?.title || false;
      }

      return {
        'cardStyle': isForm ? 'Product Tile' : (cardStyle || 'Hard Frame Tile'),
        'colorProfile' : colorProfile || (featured !==false ? 'Reversed' : 'Default'),
        'featured' : featured,
        'image' : image || featuredImage,
        'fadeTileColor': fadeTileColor,
        'content':  !isForm && 
        [{
          'content':  content || (seo?.description ? '<p>' + seo?.description + '</p>' : ''),
          'heading': heading || title,
          'eyebrow' : eyebrow,
          'linkS' : (linkS && linkS.length > 0) ? linkS : (slug ? [{ 'displayText' : getTranslation('ReadMore') ,'slug': slug, 'aria-label':  heading || title  }] : null )
        }],
        'newsLetter': isForm ? <HubSpotContent loadonscroll={true} data={{'heading': heading, ...formContent}} mobileButtonClass={`btn--secondary`}/> : false,
      };
    }

    data = convertPostTypeData(data);

    let {cardStyle, featured, colorProfile, fadeTileColor, image, content, newsLetter} = data || {};
    
    let linkClass = 'link--featured--primary';
    let css = {};
    let noLink = false;

    switch(cardStyle){
      case 'Product Tile':
        noLink = true;
        break;
      case 'Fade Tile':
        let fadeColor = colorProfile === 'Default' ? '#E2E2E2' : (fadeTileColor?.hex || '#55565A');
        if(fadeTileColor?.hex && colorProfile === 'Reversed'){
          colorProfile = 'customColor Reversed';
          linkClass = 'link--featured--secondary';
        } 
        css['--color'] = fadeColor;
        break;
      default:
    };  

    return (
      <ConditionalWrapper condition={content && content[0] && content[0]?.linkS && content[0]?.linkS[0]} wrapper={children => <Link className="tile--link" aria-label={`${getTranslation('GoTo')} ${content[0].heading.replace(/<\/?[^>]+(>|$)/g, "")}`} icon={false} data={content[0].linkS[0]}>{children}</Link>} >
        <article {...other} style={css} className={`${className} ${className}--${generateSlug(cardStyle)} ${colorProfile || 'Default'}`}>
          {
            featured && 
            <span className="btn--flag">{featured}</span>
          }
          {
            (image && cardStyle !== 'Image Tile') && <Image data={image} />
          }
          {
            content && <ContentBlock noLink={noLink} data={content[0]} className={`${className}__content ${colorProfile || 'Default'}`} linkclass={linkClass} linktag="span" />
          }
          {
            newsLetter && newsLetter
          }
        </article>
      </ConditionalWrapper>
    );
};

Tile.propTypes = {
  /**
   * Tile Class
   */
  className : PropTypes.string,
  /**
   * Class passed directly to links
   */
   data : PropTypes.shape({
     cardStyle: PropTypes.oneOf(['Product Tile', 'Fade Tile', 'Hard Frame Tile' ]),
     colorProfile: PropTypes.oneOf(['Default', 'Reversed' ]),
     content: PropTypes.array,
     fadeTileColor: PropTypes.object,
     image: PropTypes.object,
     postToDisplay: PropTypes.object,
   }),

};

Tile.defaultProps = {
  className : 'tile'
};
  

export default Tile;

export const TileFragment = graphql`
fragment TileFragment on DatoCmsComponentTile{
  cardStyle
  colorProfile
  content{
    __typename
    ...EyebrowHeadingCopyLinkFragment
    ...HubspotContentFragment
  }
  fadeTileColor {
    hex
  }
  image{
    ...ThirdImageFragment
  }
  postToDisplay{
    __typename

    ... on DatoCmsPosttypeNews{
      categories{
        originalId
        title
      }
      featuredImage{
        ...ThirdImageFragment
      }
      meta{
        createdAt(formatString: "MMMM D, YYYY")
      }
      seo{
        description
      }
      slug
      title
    }

    ... on DatoCmsPosttypeCaseStudy{
      featuredImage{
        ...ThirdImageFragment
      }
      meta{
        createdAt(formatString: "MMMM D, YYYY")
      }
      seo{
        description
      }
      slug
      title
    }
  }
}
`;
